import { 
    ref, 
    getDownloadURL, 
    uploadBytesResumable, 
    deleteObject, 
    listAll, 
    getBlob
} from "firebase/storage";
import { storage } from "./firebase";
import { updateExport } from "./database";
import { DetectedObject, ExportState } from "../types";
  
type UploadImage = (
    blob: Blob | Buffer,
    projectId: string,
    name: string
) => Promise<string | undefined>;

export const uploadImage: UploadImage = async (blob, projectId, name) => {
    try {
        const imageRef = ref(storage, `images/${projectId}/${name}`);
        const options = { 
            contentType: "image/jpeg",
            public: true
        };
        const uploadTask = uploadBytesResumable(imageRef, blob, options);
        const url: string | undefined = await new Promise((resolve, reject) => {
            const onProgress = (snapshot: any) => {
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('Upload is ' + progress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            };
            const onError = (error: any) => {
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                console.log(error);
                switch (error.code) {
                    case 'storage/unauthorized':
                    // User doesn't have permission to access the object
                    break;
                    case 'storage/canceled':
                    // User canceled the upload
                    break;
            
                    // ...
            
                    case 'storage/unknown':
                    // Unknown error occurred, inspect error.serverResponse
                    break;
                }
                reject(error);
            };
            const onComplete = () => {
                // Upload completed successfully, now we can get the download URL
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    resolve(downloadURL);
                });
            };
            uploadTask.on('state_changed', onProgress, onError, onComplete)
        })
        return url;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}


type UploadAudio = (
    blob: Blob | Buffer,
    projectId: string,
    name: string
) => Promise<string | undefined>;

export const uploadAudio: UploadAudio = async (blob, projectId, name) => {
    try {
        const audioRef = ref(storage, `audio/${projectId}/${name}`);
        const options = { 
            contentType: "audio/mpeg",
            public: true
        };
        const uploadTask = uploadBytesResumable(audioRef, blob, options);
        const url: string | undefined = await new Promise((resolve, reject) => {
            const onProgress = (snapshot: any) => {
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('Upload is ' + progress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            };
            const onError = (error: any) => {
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                console.log(error);
                switch (error.code) {
                    case 'storage/unauthorized':
                    // User doesn't have permission to access the object
                    break;
                    case 'storage/canceled':
                    // User canceled the upload
                    break;
            
                    // ...
            
                    case 'storage/unknown':
                    // Unknown error occurred, inspect error.serverResponse
                    break;
                }
                reject(error);
            };
            const onComplete = () => {
                // Upload completed successfully, now we can get the download URL
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    resolve(downloadURL);
                });
            };
            uploadTask.on('state_changed', onProgress, onError, onComplete)
        })
        return url;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type DownloadImage = (
    projectId: string,
    name: string
) => Promise<string>;

export const downloadImage: DownloadImage = async (projectId, name) => {
    let url = '';
    try {
        const imageRef = ref(storage, `images/${projectId}/${name}`);
        url = await getDownloadURL(imageRef);
    } catch (e) {
        console.error(e);
    }
    return url;
}


type UploadDetectedObjects = (
    detectedObjects: DetectedObject[],
    projectId: string,
) => Promise<string | undefined>;

export const uploadDetectedObjects: UploadDetectedObjects = async (
    detectedObjects, 
    projectId
) => {
    try {
        const detectedObjectsRef = ref(storage, `detectedObjects/${projectId}.json`);
        const options = { 
            contentType: "application/json",
            public: true
        };
        const blob = Buffer.from(JSON.stringify(detectedObjects));
        const uploadTask = uploadBytesResumable(detectedObjectsRef, blob, options);
        const url: string | undefined = await new Promise((resolve, reject) => {
            const onProgress = (snapshot: any) => {
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('Upload is ' + progress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            };
            const onError = (error: any) => {
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                console.log(error);
                switch (error.code) {
                    case 'storage/unauthorized':
                    // User doesn't have permission to access the object
                    break;
                    case 'storage/canceled':
                    // User canceled the upload
                    break;
            
                    // ...
            
                    case 'storage/unknown':
                    // Unknown error occurred, inspect error.serverResponse
                    break;
                }
                reject(error);
            };
            const onComplete = () => {
                // Upload completed successfully, now we can get the download URL
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    resolve(downloadURL);
                });
            };
            uploadTask.on('state_changed', onProgress, onError, onComplete)
        })
        return url;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type GetDetectedObjectsUrl = (
    projectId: string,
) => Promise<string | undefined>;

export const getDetectedObjectsUrl: GetDetectedObjectsUrl = async (projectId) => {
    try {
        const detectedObjectsRef = ref(storage, `detectedObjects/${projectId}.json`);
        const url = await getDownloadURL(detectedObjectsRef);
        return url;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type DownloadDetectedObjects = (
    projectId: string,
) => Promise<DetectedObject[]>;

export const downloadDetectedObjects: DownloadDetectedObjects = async (projectId) => {
    try {
        const detectedObjectsRef = ref(storage, `detectedObjects/${projectId}.json`);
        const blob = await getBlob(detectedObjectsRef);
        return JSON.parse(await blob.text()) as DetectedObject[];
    } catch (e) {
        console.error(e);
    }
    return [];
}

type DeleteImage = (
    projectId: string,
    name: string
) => void;

export const deleteImage: DeleteImage = async (projectId, name) => {
    const imageRef = ref(storage, `images/${projectId}/${name}`);
    await deleteObject(imageRef);
}

type DeleteProjectImages = (
    projectId: string,
) => void;

export const deleteProjectImages: DeleteProjectImages = async (projectId) => {
    const videoRef = ref(storage, `images/${projectId}`);
    const res = await listAll(videoRef);
    res.items.forEach(itemRef => {
        deleteObject(itemRef);
    });
}

type UploadVideo = (
    uri: Blob | ArrayBuffer,
    exportState: ExportState,
    onProgress?: (progress: number) => void
) => Promise<string | undefined>;

export const uploadVideo: UploadVideo = async (blob, exportState, _onProgress) => {
    try {
        const name = `${exportState.exportId}.mp4`;
        const videoRef = ref(storage, `videos/${exportState.projectId}/${name}`);
        const options = { 
            contentType: "video/mp4",
            public: true
        };
        const uploadTask = uploadBytesResumable(videoRef, blob, options);
        const url: string | undefined = await new Promise((resolve, reject) => {
            const onProgress = (snapshot: any) => {
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) 
                _onProgress?.(progress);
                const percent = progress * 100;

                exportState.process = "uploading";
                exportState.progress = progress;
                updateExport(exportState);
                
                console.log('Upload is ' + percent + '% done');
                switch (snapshot.state) {
                    case 'paused':
                    console.log('Upload is paused');
                    break;
                    case 'running':
                    console.log('Upload is running');
                    break;
                }
            };
            const onError = (error: any) => {
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                console.log(error);
                switch (error.code) {
                    case 'storage/unauthorized':
                    // User doesn't have permission to access the object
                    break;
                    case 'storage/canceled':
                    // User canceled the upload
                    break;
            
                    // ...
            
                    case 'storage/unknown':
                    // Unknown error occurred, inspect error.serverResponse
                    break;
                }
                reject(error);
            };
            const onComplete = () => {
                // Upload completed successfully, now we can get the download URL
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    resolve(downloadURL);
                });
            };
            uploadTask.on('state_changed', onProgress, onError, onComplete)
        })
        return url;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type DeleteVideo = (
    projectId: string,
    exportId: string
) => void;

export const deleteVideo: DeleteVideo = async (projectId, exportId) => {
    const name = `${exportId}.mp4`;
    const videoRef = ref(storage, `videos/${projectId}/${name}`);
    await deleteObject(videoRef);
}


type DeleteProjectVideos = (
    projectId: string,
) => void;

export const deleteProjectVideos: DeleteProjectVideos = async (projectId) => {
    const videoRef = ref(storage, `videos/${projectId}`);
    const res = await listAll(videoRef);
    res.items.forEach(itemRef => {
        deleteObject(itemRef);
    });
}